import { useMemo } from "react";
import { Download, Info, Tag } from "lucide-react";
import { Button } from "../components/ui/Button";
import { EmptyState } from "../components/ui/EmptyState";
import { PageHeader } from "../components/ui/PageHeader";
import { PortalState } from "../components/ui/PortalState";
import { apiGet, downloadBlob } from "../api/portal-client";
import { usePortalResource } from "../hooks/usePortalResource";
import { DASH, formatEuro, toFiniteNumber } from "../lib/format";

interface PriceRow {
  id: string;
  productId: string | null;
  productName: string;
  priceHT: number;
  unit: string | null;
  vatPct: number | null;
  pvc: number | null;
  pvcSource: "perso" | "coeff" | "catalogue" | null;
  pvcCoeff: number | null;
  general: boolean;
}

/** Origine du prix conseillé, dite en clair au client. */
const PVC_SOURCE_LABELS: Record<NonNullable<PriceRow["pvcSource"]>, string> = {
  perso: "PVC négocié",
  coeff: "calculé avec votre coefficient",
  catalogue: "PVC public du catalogue",
};

/**
 * Mes tarifs.
 *
 * W7-B — « Aucun tarif configuré » s'affichait aussi bien pour une grille
 * réellement vide que pour une lecture échouée, et `eur.format(null)` rendait
 * « 0,00 € » : un prix manquant se lisait GRATUIT. Sur une grille tarifaire,
 * c'est la donnée la plus sensible du portail — un client peut commander en
 * s'appuyant dessus. Prix inconnu = « — ».
 */
export function PricesPage() {
  const { data, status, error, reload } = usePortalResource<PriceRow[]>(() =>
    apiGet<PriceRow[]>("/me/prices"),
  );
  const rows = useMemo(() => data ?? [], [data]);

  // Repli P7 : toutes les lignes viennent du catalogue → aucune grille négociée.
  const isGeneral = rows.length > 0 && rows.every((r) => r.general);
  const pvcCoeff = useMemo(() => rows.find((r) => r.pvcCoeff != null)?.pvcCoeff ?? null, [rows]);

  async function handleDownload() {
    await downloadBlob("/me/prices/download", "tarifs.pdf");
  }

  return (
    <main className="max-w-4xl mx-auto px-4 py-6">
      <PageHeader
        title="Mes tarifs"
        subtitle="La grille appliquée à votre compte par votre fournisseur."
        actions={
          // Un PDF d'une grille vide n'aurait rien à imprimer : le seul CTA de
          // la page n'apparaît qu'une fois la grille réellement lue.
          status === "ready" && rows.length > 0 ? (
            <Button onClick={handleDownload} variant="secondary">
              <Download size={16} /> Télécharger en PDF
            </Button>
          ) : undefined
        }
      />

      <PortalState status={status} label="vos tarifs" error={error} onRetry={reload} />

      {isGeneral && (
        <p className="flex items-start gap-2 bg-card border border-border rounded-xl2 px-4 py-3 text-sm text-muted mb-4">
          <Info size={16} className="shrink-0 mt-0.5 text-terracotta" />
          <span>
            <span className="font-semibold text-ink">Tarifs généraux</span> — aucun tarif négocié n'est
            enregistré pour votre compte : voici les prix du catalogue de votre fournisseur.
          </span>
        </p>
      )}

      {pvcCoeff != null && (
        <p className="text-xs text-muted mb-4">
          Coefficient de PVC appliqué à votre compte : <span className="font-mono text-ink">{pvcCoeff}</span>
        </p>
      )}

      {status === "ready" && rows.length === 0 && (
        <EmptyState
          icon={Tag}
          title="Aucun tarif pour l'instant"
          description="Votre fournisseur n'a encore publié aucune grille pour votre compte."
          action={{ to: "/messages", label: "Demander ma grille au fournisseur" }}
        />
      )}

      {status === "ready" && rows.length > 0 && (
        <div className="bg-card border border-border rounded-xl2 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-cream border-b border-border">
              <tr>
                <th className="text-left px-4 py-3 font-semibold text-ink">Produit</th>
                <th className="text-right px-4 py-3 font-semibold text-ink">Votre prix HT</th>
                <th className="text-right px-4 py-3 font-semibold text-ink">PVC conseillé</th>
                <th className="text-right px-4 py-3 font-semibold text-ink">Unité</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                // Le PVC est un prix de revente au consommateur : on l'affiche TTC dès
                // que la TVA du produit est connue, et on le dit quand elle ne l'est pas.
                const pvc = toFiniteNumber(r.pvc);
                const pvcTtc = pvc != null && r.vatPct != null ? pvc * (1 + r.vatPct / 100) : null;
                return (
                  <tr key={r.id} className="border-b border-border last:border-0">
                    <td className="px-4 py-3 text-ink">{r.productName}</td>
                    <td className="px-4 py-3 text-right font-mono text-ink">{formatEuro(r.priceHT)}</td>
                    <td className="px-4 py-3 text-right">
                      {pvc == null ? (
                        <span className="text-muted">{DASH}</span>
                      ) : (
                        <span className="flex flex-col items-end">
                          <span className="font-mono text-ink">{formatEuro(pvcTtc ?? pvc)}</span>
                          <span className="text-xs text-muted">
                            {pvcTtc != null ? "TTC" : "HT (TVA inconnue)"}
                            {r.pvcSource ? ` · ${PVC_SOURCE_LABELS[r.pvcSource]}` : ""}
                          </span>
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right text-muted">{r.unit ?? DASH}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </main>
  );
}
